import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { startCase, toLower } from 'lodash';
import { ISectionCountProps } from './types';

const { locale } = new Intl.NumberFormat().resolvedOptions();

// https://www.youtube.com/watch?v=re2JFITR7TI&ab_channel=ByteGrad
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

const dateReviver = (key: string, value: any) => {
  let a;
  if (typeof value === 'string') {
    a = Date.parse(value);
    if (a) {
      return new Date(a);
    }
  }
  return value;
};
export const jsonParseWithDates = (str: string) => JSON.parse(str, dateReviver);

/**
 * @param amount
 * @param currency
 * @returns
 */
export const formatMoney = (amount: number, currency: string) => (amount || amount === 0 ? `${currency} ${formatNumber(amount)}` : '');

/**
 * @param value
 * @returns
 */
export const formatNumber = (value: number) =>
  value || value === 0
    ? value.toLocaleString(locale, {
        style: 'decimal',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    : '';

export const nFormatter = (num: number, digits: number) => {
  const lookup = [
    { value: 1, symbol: '' },
    { value: 1e3, symbol: 'K' },
    { value: 1e6, symbol: 'M' },
    { value: 1e9, symbol: 'G' },
    { value: 1e12, symbol: 'T' },
    { value: 1e15, symbol: 'P' },
    { value: 1e18, symbol: 'E' },
  ];
  const regexp = /\.0+$|(?<=\.[0-9]*[1-9])0+$/;
  const sign = num < 0 ? '-' : '';
  const absNum = Math.abs(num);
  const item = lookup
    .slice()
    .reverse()
    .find((item) => absNum >= item.value);
  return item ? sign + (absNum / item.value).toFixed(digits).replace(regexp, '').concat(item.symbol) : '0';
};

/**
 * @param value
 * @returns
 */
export const SimpleFormatNumber = (value?: number, isHideDecimal?: boolean, digits?: number) => {
  if (!value && value !== 0) return 0;
  let options: Record<string, any> = {
    style: 'decimal',
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  };

  if (isHideDecimal) {
    options = {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
      ...options,
    };
  }
  return value.toLocaleString(locale, options);
};

export const byteConverter = (bytes: number, decimals: number = 2) => {
  const K_UNIT = 1024;
  const SIZES = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB'];

  if (bytes === 0) {
    return '0 Byte';
  }
  const i = Math.floor(Math.log(bytes) / Math.log(K_UNIT));
  const result = `${parseFloat((bytes / K_UNIT ** i).toFixed(decimals))} ${SIZES[i]}`;

  return result;
};

export const beautifyEnumWithTHC = (value: string) => {
  if (value === 'THC') {
    return value;
  }
  return beautifyEnum(value);
};

export const beautifyEnum = (name: string) =>
  name
    ? name
        .replace(/_/g, ' ')
        .toLowerCase()
        .replace(/\b\w/g, (char) => char.toUpperCase())
    : '';

/**
 * Formats status labels with custom mappings for specific statuses.
 * Maps "NOT_AN_INVOICE" and "RESOLVED_AS_NOT_AN_INVOICE" to "Invalid File" variants.
 *
 * @param status - The status string to format
 * @returns The formatted status label
 */
export const getStatusLabel = (status: string): string => {
  if (!status) return '';

  // Custom mappings for specific statuses
  const statusMappings: Record<string, string> = {
    NOT_AN_INVOICE: 'Invalid File',
    RESOLVED_AS_NOT_AN_INVOICE: 'Resolved As Invalid File',
  };

  // Return custom mapping if it exists, otherwise use beautifyEnum
  return statusMappings[status] || beautifyEnum(status);
};

export const camelCaseToWords = (text: string) => {
  const result = text.replace(/([A-Z])/g, ' $1');
  const finalResult = result.charAt(0).toUpperCase() + result.slice(1);
  return finalResult;
};

export const booleanToYesNo = (value: boolean | string) => {
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }
  if (typeof value === 'string') {
    return value.toLowerCase() === 'true' ? 'Yes' : 'No';
  }
  return value;
};

export const isJsonString = (str: string) => {
  try {
    JSON.parse(str);
  } catch (e) {
    return false;
  }
  return true;
};

type DisplayOption = number | string | null | undefined;
export const displayValueOrFallback = (value: DisplayOption, fallback: string = '00.00'): string => {
  if (value === null || value === undefined || value === '') {
    return fallback;
  }
  if (typeof value === 'number') {
    return formatNumber(value);
  }
  if (typeof value === 'string') {
    return value;
  }
  return fallback;
};

export function keepFileAlphanumeric(name: string) {
  const pattern = /[^0-9a-zA-Z.]/g;
  return name.replace(pattern, '');
}

export const capitalizeName = (name: string): string => startCase(toLower(name));

export const objectCountToArray = (data: Record<string, number>): ISectionCountProps => {
  const items = [];
  if (data) {
    for (const [key, value] of Object.entries(data)) {
      items.push({
        name: key,
        label: beautifyEnum(key),
        value,
      });
    }
  }
  const total = items.reduce((total, item) => total + item.value, 0);
  return {
    total,
    items,
  };
};

/**
 * Removes double quotes (`""`) from a string.
 * If the input is not a string, it returns the input as-is.
 *
 * @param str - The input string (or other types like number, null, undefined).
 * @returns The input without double quotes, or the original input if it's not a string.
 */
export const removeQuotes = (str: string | number | null | undefined): string | number | null | undefined => {
  if (typeof str === 'string') {
    return str.replace(/"/g, ''); // Remove all double quotes
  }
  return str; // Return the original value if it's not a string
};

// Utility function to transform titles (or consignee names)
export const transformTitle = (title: string): string => {
  const titleMap: Record<string, string> = {
    'Ultra Clean Asia Pacific, Pte Ltd': 'UCAP',
    'Ultra Clean Technology (Malaysia) SDN. BHD.': 'UCTM',
    // Add more mappings as needed
  };

  return titleMap[title] || title;
};

const getNumber = (val: number | undefined | null) => val || 0;

/**
 * Get color class for deviation amounts based on value
 * Used for Net Deviations card and other deviation displays
 *
 * Negative values = favorable deviation (savings) = green
 * Positive values = unfavorable deviation (overspending) = red
 * Zero values = neutral = black
 *
 * @param amount - The deviation amount value
 * @returns Tailwind CSS color class
 *
 * @example
 * getDeviationAmountColor(-3124.00) // returns 'text-[#00992b]' (green - favorable)
 * getDeviationAmountColor(100.00)   // returns 'text-[#E72547]' (red - unfavorable)
 */
export const getDeviationAmountColor = (amount: number): string => {
  const value = getNumber(amount);
  return value < 0 ? 'text-[#00992b]' : value > 0 ? 'text-[#E72547]' : 'text-neutral-100';
};
